<template>
  <div class="tunnel-payment-method">
    <h2 class="text-h6 mb-2">{{ $t('onboarding.tunnel.step.payment-method') }}</h2>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <!-- Revenue ici après un acompte refusé : le motif de la banque, et de quoi donner un autre moyen. -->
    <v-alert v-else-if="depositFailure" type="warning" dense outlined class="mb-4">
      {{ $t('onboarding.tunnel.deposit.refused', { reason: depositFailure }) }}
    </v-alert>

    <!-- Avant de demander quoi que ce soit : pourquoi, quand et combien. -->
    <v-card v-if="!mandateHtml" outlined class="pa-4 mb-5 tunnel-payment-method__why">
      <div class="font-weight-medium mb-2">{{ $t('onboarding.tunnel.payment.why.title') }}</div>
      <ul class="text-body-2">
        <li>{{ $t('onboarding.tunnel.payment.why.who', { organization }) }}</li>
        <li>{{ $t(`onboarding.tunnel.payment.why.when.${whenKey}`) }}</li>
        <li>{{ todayText }}</li>
        <li>{{ $t('onboarding.tunnel.payment.why.suspended') }}</li>
        <li>{{ $t('onboarding.tunnel.payment.why.secure') }}</li>
      </ul>
    </v-card>

    <!-- Le choix, seulement quand l'organisation en autorise plusieurs, et pas devant un mandat à signer. -->
    <v-radio-group
        v-if="instruments.length > 1 && !mandateHtml"
        v-model="instrument"
        row
        dense
        hide-details
        class="mt-0 mb-4"
        :disabled="working"
    >
      <v-radio v-for="i in instruments" :key="i" :value="i" :label="$t(`onboarding.tunnel.payment.instrument.${i}`)" />
    </v-radio-group>

    <!-- SEPA — 1. L'IBAN : saisi dans le champ de Stripe, il ne passe jamais par nos serveurs. -->
    <template v-if="instrument === 'sepa_debit' && !mandateHtml">
      <p class="text--secondary mb-4">{{ $t('onboarding.tunnel.payment.iban-explained') }}</p>
      <div class="text-subtitle-2 mb-2">{{ $t('onboarding.tunnel.payment.iban') }}</div>
      <div id="tunnel-iban-element" class="stripe-element pa-3" />
      <div v-if="elementError" class="error--text text-caption mt-1">{{ elementError }}</div>
      <p class="text-caption text--secondary mt-3">{{ $t('onboarding.tunnel.payment.sepa-notice') }}</p>

      <div class="d-flex mt-4">
        <v-spacer />
        <v-btn small color="primary" :loading="working" :disabled="!elementComplete" @click="preview">
          {{ $t('onboarding.tunnel.continue') }}
        </v-btn>
      </div>
    </template>

    <!-- SEPA — 2. Le mandat tel qu'il sera signé. -->
    <template v-else-if="instrument === 'sepa_debit'">
      <p class="text--secondary mb-3">{{ $t('onboarding.tunnel.payment.mandate-explained') }}</p>
      <div class="mandate-box pa-4 mb-4" v-html="mandateHtml" />
      <v-checkbox v-model="accepted" :label="$t('onboarding.tunnel.payment.accept')" dense hide-details class="mb-4" />

      <div class="d-flex mt-4">
        <v-btn small text :disabled="working" @click="changeIban">{{ $t('onboarding.tunnel.payment.change-iban') }}</v-btn>
        <v-spacer />
        <v-btn small color="primary" :loading="working" :disabled="!accepted" @click="sign">
          {{ $t('onboarding.tunnel.payment.sign') }}
        </v-btn>
      </div>
    </template>

    <!-- Carte : saisie dans le champ de Stripe ; la banque peut demander une authentification (3-D Secure). -->
    <template v-else-if="instrument === 'card'">
      <p class="text--secondary mb-4">{{ $t('onboarding.tunnel.payment.card-explained') }}</p>
      <div class="text-subtitle-2 mb-2">{{ $t('onboarding.tunnel.payment.card') }}</div>
      <div id="tunnel-card-element" class="stripe-element pa-3" />
      <div v-if="elementError" class="error--text text-caption mt-1">{{ elementError }}</div>
      <p class="text-caption text--secondary mt-3">{{ $t('onboarding.tunnel.payment.card-notice') }}</p>

      <div class="d-flex mt-4">
        <v-spacer />
        <v-btn small color="primary" :loading="working" :disabled="!elementComplete" @click="saveCard">
          {{ $t('onboarding.tunnel.payment.save-card') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import StripeService from '@service/StripeService'

const ELEMENT_STYLE = {
  base: { fontSize: '16px', color: '#32325d', '::placeholder': { color: '#aab7c4' } },
  invalid: { color: '#fa755a' },
}

/**
 * La porte « moyen de paiement » du tunnel, avec les instruments que l'organisation autorise :
 *  - SEPA : un IBAN, l'aperçu du mandat, la signature. Le mandat signé est exactement celui
 *    montré : le serveur garde l'aperçu et signe celui-là ;
 *  - carte : Stripe l'enregistre (et fait passer le 3-D Secure) ; le serveur relit l'issue chez
 *    Stripe, il ne croit pas le navigateur sur parole.
 */
export default {
  name: 'TunnelPaymentMethod',

  props: {
    token: { type: String, required: true },
    holderName: { type: String, required: true },
    email: { type: String, required: true },
    instruments: { type: Array, required: true },
    depositFailure: { type: String, default: null },
    organization: { type: String, required: true },
    /** Comment les recharges seront facturées : { mode, collectionMethod } */
    billing: { type: Object, default: null },
    deposit: { type: Object, default: null },
    /** Le dossier passe par l'accord de l'organisation : l'acompte ne part qu'à l'acceptation. */
    requiresApproval: { type: Boolean, default: false },
  },

  data() {
    return {
      instrument: this.instruments[0] || null,
      stripe: null,
      element: null,
      elementComplete: false,
      elementError: null,
      mandateHtml: null,
      accepted: false,
      working: false,
      error: null,
    }
  },

  computed: {
    /** Ce qui est débité aujourd'hui : rien, l'acompte à l'étape suivante, ou l'acompte à l'acceptation. */
    todayText() {
      if (!this.deposit || !this.deposit.amount) {
        return this.$t('onboarding.tunnel.payment.why.today-nothing')
      }
      const params = { amount: this.money(this.deposit.amount), organization: this.organization }
      return this.$t(this.requiresApproval ? 'onboarding.tunnel.payment.why.today-deposit-on-approval' : 'onboarding.tunnel.payment.why.today-deposit', params)
    },

    /** La phrase « quand » : au fil des recharges, par seuil, ou une facture mensuelle prélevée ou à virer. */
    whenKey() {
      const mode = this.billing?.mode
      if (mode === 'monthly') return this.billing.collectionMethod === 'auto-debit' ? 'monthly-debit' : 'monthly'
      if (mode === 'threshold') return 'threshold'
      return 'pay-per-use'
    },
  },

  watch: {
    instrument() {
      this.error = null
      this.mountElement()
    },
  },

  mounted() {
    this.mountElement()
  },

  beforeDestroy() {
    this.destroyElement()
  },

  methods: {
    money(amount) {
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: 'EUR' }).format(amount)
    },

    /** Le champ Stripe de l'instrument choisi : IBAN ou carte. */
    async mountElement() {
      try {
        this.stripe = this.stripe || await StripeService.initStripe()
        this.$nextTick(() => {
          this.destroyElement()
          const card = this.instrument === 'card'
          this.element = card
            ? this.stripe.elements().create('card', { style: ELEMENT_STYLE, hidePostalCode: true })
            : this.stripe.elements().create('iban', { supportedCountries: ['SEPA'], style: ELEMENT_STYLE })
          this.element.mount(card ? '#tunnel-card-element' : '#tunnel-iban-element')
          this.element.on('change', (event) => {
            this.elementComplete = event.complete
            this.elementError = event.error ? event.error.message : null
          })
        })
      } catch (e) {
        this.error = this.$t('onboarding.tunnel.payment.stripe-unavailable')
      }
    },

    destroyElement() {
      if (this.element) {
        this.element.destroy()
        this.element = null
      }
      this.elementComplete = false
      this.elementError = null
    },

    async preview() {
      this.working = true
      this.error = null
      try {
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
          type: 'sepa_debit',
          sepa_debit: this.element,
          billing_details: { name: this.holderName, email: this.email },
        })
        if (error) {
          this.elementError = error.message
          return
        }
        const result = await RegistrationRepository.previewMandate(this.token, paymentMethod.id)
        this.destroyElement()
        this.mandateHtml = result.mandateHtml
        this.accepted = false
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    changeIban() {
      this.mandateHtml = null
      this.mountElement()
    },

    async sign() {
      this.working = true
      this.error = null
      try {
        this.$emit('collected', await RegistrationRepository.signMandate(this.token))
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    async saveCard() {
      this.working = true
      this.error = null
      try {
        const { clientSecret } = await RegistrationRepository.startCardSetup(this.token)
        const { error } = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: this.element, billing_details: { name: this.holderName, email: this.email } },
        })
        if (error) {
          // Refus de la banque, authentification abandonnée : les mots de Stripe, sous le champ.
          this.elementError = error.message
          return
        }
        this.$emit('collected', await RegistrationRepository.confirmCard(this.token))
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      return key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred')
    },
  },
}
</script>

<style scoped>
.tunnel-payment-method__why ul {
  padding-left: 18px;
}
.tunnel-payment-method__why li {
  margin-bottom: 4px;
}
.stripe-element {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
}
.mandate-box {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
}
</style>
